/**
 * LogoUploader — file picker + preview + upload + remove for the
 * organisation logo. Used inside the Organisation tab of OrganisationSetup.
 *
 * Props:
 *   logoSrc   string|null  — base64 data URL ("data:image/...") or null
 *   canEdit   boolean
 *   onUploaded  (updatedOrgRow) => void
 *   onRemoved   (updatedOrgRow) => void
 */

import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApi } from '../../lib/api';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/svg+xml';
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB raw — canvas will compress down

// Compress raster images to ≤800×800 JPEG at 85% quality before upload.
// SVGs are passed through unchanged (already compact vector format).
// Keeps the upload well under DO's nginx 1 MB proxy limit.
function compressImage(file) {
  if (file.type === 'image/svg+xml') return Promise.resolve(file);
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const MAX = 800;
      const scale = Math.min(MAX / img.width, MAX / img.height, 1);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error('Image compression failed'));
          resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
        },
        'image/jpeg',
        0.85,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Could not read image')); };
    img.src = objectUrl;
  });
}

export default function LogoUploader({ logoSrc, canEdit, onUploaded, onRemoved }) {
  const { appId } = useParams();
  const api = useApi();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [imgBroken, setImgBroken] = useState(false);

  const handlePick = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setError('File too large (max 10 MB).');
      e.target.value = '';
      return;
    }
    upload(file);
  };

  const upload = async (file) => {
    setError(null);
    setUploading(true);
    setImgBroken(false);
    try {
      const compressed = await compressImage(file);
      const updated = await api.uploadLogo(appId, compressed);
      onUploaded?.(updated);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    setError(null);
    setUploading(true);
    try {
      const updated = await api.deleteLogo(appId);
      onRemoved?.(updated);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Remove failed');
    } finally {
      setUploading(false);
    }
  };

  const showLogo = logoSrc && !imgBroken;

  return (
    <div className="logo-uploader">
      <div className="logo-preview">
        {showLogo ? (
          <img
            src={logoSrc}
            alt="Organisation logo"
            onError={() => setImgBroken(true)}
          />
        ) : (
          <div className="logo-preview-empty">
            <span>No logo uploaded</span>
          </div>
        )}
      </div>
      {canEdit && (
        <div className="logo-actions">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            style={{ display: 'none' }}
            onChange={handlePick}
          />
          <button
            type="button"
            className="btn-secondary"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'Uploading…' : showLogo ? 'Replace logo' : 'Upload logo'}
          </button>
          {showLogo && (
            <button
              type="button"
              className="btn-link-sm danger"
              onClick={handleRemove}
              disabled={uploading}
            >
              Remove
            </button>
          )}
        </div>
      )}
      <p className="form-help" style={{ margin: 0 }}>
        JPEG, PNG, WebP or SVG · auto-compressed before upload
      </p>
      {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
}
